import React from "react";

export interface MacroValue {
  actual: number;
  goal: number;
}

export type MealCell =
  | {
      status: "logged";
      time: string;
      cal: MacroValue;
      pro: MacroValue;
      carb: MacroValue;
      fat: MacroValue;
    }
  | { status: "skipped" }
  | { status: "notLogged" }
  | { status: "na" }
  | { status: "none" };

export interface MealRow {
  meal: string;
  days: MealCell[]; // one per day column
}

export interface DayHeader {
  day: string;
  tag: string; // "Regular", "Detox", "Travel", etc.
}

interface MealWiseMacrosProps {
  days?: DayHeader[];
  rows?: MealRow[];
}

const CAL_COLOR = "#B44AE6";
const PRO_COLOR = "#6BBDFA";
const CARB_COLOR = "#FF9500";
const FAT_COLOR = "#F25BEC";


// Shared goal-line position for all bars (percent of track width).
// Values from 0 → goal fill the range 0% → GOAL_POSITION_PCT.
// Anything above goal extends into the OVERFLOW_PCT zone and caps there,
// regardless of how much the actual exceeds the goal.
const GOAL_POSITION_PCT = 85;
const OVERFLOW_PCT = 15;

const MacroLine: React.FC<{ label: string; value: MacroValue; color: string }> = ({
  label,
  value,
  color,
}) => {
  const ratio = value.goal > 0 ? value.actual / value.goal : 0;
  const overGoal = ratio > 1;
  const fillPct = overGoal
    ? GOAL_POSITION_PCT + OVERFLOW_PCT
    : ratio * GOAL_POSITION_PCT;

  return (
    <div className="flex items-center gap-2 w-full">
      <span className="text-[8px] 2xl:text-[10px] 3xl:text-[11px] text-[#8D8D8D] font-inter w-6 flex-shrink-0">
        {label}
      </span>
      <div className="relative flex-1 h-1.5">
        {/* Fill */}
        <div
          className="absolute top-0 left-0 h-full rounded-full transition-all"
          style={{ width: `${fillPct}%`, background: color }}
        />
        {/* Goal line marker (vertical tick) */}
        <div
          className="absolute w-px bg-[#8D8D8D]"
          style={{
            left: `${GOAL_POSITION_PCT}%`,
            top: -2,
            bottom: -2,
          }}
        />
      </div>
      <span className="text-[8px] 2xl:text-[10px] 3xl:text-[11px] font-inter font-medium tabular-nums w-9 2xl:w-11 text-right flex-shrink-0 text-[#1F1F1F]">
        <span className={overGoal ? "text-[#FF3B30]" : undefined}>
          {value.actual}
        </span>
        /{value.goal}
      </span>
    </div>
  );
};

const MealCellView: React.FC<{ cell: MealCell }> = ({ cell }) => {
  if (cell.status === "skipped") {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-[12px] 3xl:text-[13px] text-[#FF3B30] font-inter font-medium">
          Skipped
        </span>
      </div>
    );
  }
  if (cell.status === "notLogged") {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-[12px] 3xl:text-[13px] text-[#1F1F1F] font-inter font-medium">
          Not logged
        </span>
      </div>
    );
  }
  if (cell.status === "na") {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-[12px] 3xl:text-[13px] text-[#8D8D8D] font-inter font-medium">
          NA
        </span>
      </div>
    );
  }
  if (cell.status === "none") {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-[12px] 3xl:text-[13px] text-[#8D8D8D] font-inter">—</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 py-2">
      <div className="text-[9px] 2xl:text-[11px] 3xl:text-[12px] text-[#0DB5B5] font-inter font-semibold">
        {cell.time}
      </div>
      <MacroLine label="Cal" value={cell.cal} color={CAL_COLOR} />
      <MacroLine label="Pro" value={cell.pro} color={PRO_COLOR} />
      <MacroLine label="Carb" value={cell.carb} color={CARB_COLOR} />
      <MacroLine label="Fat" value={cell.fat} color={FAT_COLOR} />
    </div>
  );
};

const MealWiseMacros: React.FC<MealWiseMacrosProps> = ({
  days = [],
  rows = [],
}) => {
  const legend = [
    { label: "Calories", color: CAL_COLOR },
    { label: "Adj Protein", color: PRO_COLOR },
    { label: "Carb", color: CARB_COLOR },
    { label: "Fat", color: FAT_COLOR },
  ];

  // 1 label column + N day columns.
  // Meal label column is narrower on smaller screens so day cells get more room.
  const gridTemplate = `var(--meal-col, 90px) repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full [--meal-col:90px] 2xl:[--meal-col:120px] 3xl:[--meal-col:140px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Meal-Wise Macros
        </span>
        <div className="flex items-center gap-4">
          {legend.map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full inline-block"
                style={{ background: l.color }}
              />
              <span className="text-[11px] 3xl:text-[13px] text-[#1F1F1F] font-inter">
                {l.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#F3F4F6] rounded-lg overflow-hidden">
        {/* Header row */}
        <div
          className="grid bg-[#FAFAFA] border-b border-[#F3F4F6]"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <div className="px-3 py-2.5 text-[11px] 3xl:text-[12px] font-semibold uppercase tracking-wide text-[#1F1F1F] font-inter">
            Meal
          </div>
          {days.map((d) => (
            <div
              key={d.day}
              title={`${d.day} - ${d.tag}`}
              className="px-3 py-2.5 text-[11px] 3xl:text-[12px] font-semibold uppercase tracking-wide text-[#1F1F1F] font-inter border-l border-[#F3F4F6] truncate whitespace-nowrap"
            >
              {d.day} - <span className="text-[#8D8D8D] font-medium normal-case">{d.tag}</span>
            </div>
          ))}
        </div>

        {/* Body rows */}
        {rows.map((row, ri) => (
          <div
            key={row.meal}
            className={`grid ${ri < rows.length - 1 ? "border-b border-[#F3F4F6]" : ""}`}
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <div className="px-3 py-2.5 text-[12px] 3xl:text-[13px] text-[#1F1F1F] font-inter font-medium flex items-center">
              {row.meal}
            </div>
            {row.days.map((cell, ci) => (
              <div
                key={ci}
                className="px-2 py-1 border-l border-[#F3F4F6] flex items-stretch"
              >
                <div className="w-full">
                  <MealCellView cell={cell} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MealWiseMacros;
